import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { RecaptchaNotice } from "@/components/RecaptchaNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => {
    try { return JSON.parse(localStorage.getItem("findloop:loginEmails") || "[]")[0] || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuggest, setShowSuggest] = useState(true);

  // Adresses mémorisées localement lors de connexions précédentes (ce navigateur).
  const [savedEmails, setSavedEmails] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("findloop:loginEmails") || "[]");
      return Array.isArray(raw) ? raw.filter((x) => typeof x === "string") : [];
    } catch { return []; }
  });

  function rememberEmail(addr) {
    const next = [addr, ...savedEmails.filter((x) => x !== addr)].slice(0, 5);
    setSavedEmails(next);
    try { localStorage.setItem("findloop:loginEmails", JSON.stringify(next)); } catch { /* ignore */ }
  }

  // Suggestion affichée UNIQUEMENT si les premières lettres saisies correspondent
  // à une adresse mémorisée (et qu'elle n'est pas déjà saisie en entier).
  const suggestions = useMemo(() => {
    const typed = email.trim().toLowerCase();
    if (!typed) return [];
    return savedEmails.filter((a) => a.toLowerCase().startsWith(typed) && a.toLowerCase() !== typed);
  }, [email, savedEmails]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const normalized = email.trim().toLowerCase();
      const captchaToken = await getRecaptchaToken("login");
      const partner = await login(normalized, password, captchaToken, remember);
      rememberEmail(normalized);
      toast.success(t("hc.welcome"));
      const next = searchParams.get("next");
      if (next && next.startsWith("/") && !partner?.is_superadmin) navigate(next);
      else navigate(partner?.is_superadmin ? "/superadmin" : "/b2b/registry");
    } catch (err) {
      const msg = err.message || t("hc.loginFailed");
      if (err.status === 429) {
        // Lockout: show the countdown ("Réessayez dans X minute(s)") briefly, then auto-hide.
        toast.warning(msg, { duration: 7000 });
      } else if (/mot de passe incorrect/i.test(msg)) {
        toast.error(msg, {
          action: { label: t("hc.reset"), onClick: () => navigate("/auth/forgot") },
          duration: 9000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title={t("a.loginTitle")} subtitle={t("a.loginSub")}>
      <form onSubmit={submit} className="findo-form-fields space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">{t("a.email")}</Label>
          <div className="relative">
            <Input id="email" type="email" required value={email} autoComplete="off"
              onChange={(e) => { setEmail(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              onKeyDown={(e) => { if (e.key === "Escape") setShowSuggest(false); }}
              placeholder={t("hc.youBusinessCom")} className="mt-1.5 h-11 rounded-xl" data-testid="login-email" />
            {showSuggest && suggestions.length > 0 && (
              <ul className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-border bg-white shadow-lg overflow-hidden"
                data-testid="login-email-suggestions">
                {suggestions.map((addr) => (
                  <li key={addr}>
                    <button type="button" data-testid="login-email-suggestion"
                      onMouseDown={(e) => { e.preventDefault(); setEmail(addr); setShowSuggest(false); }}
                      className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-findo-indigo/5 transition-colors">
                      {addr}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("a.password")}</Label>
            <Link to="/auth/forgot" className="text-xs font-semibold text-findo-indigo hover:underline">{t("a.forgot")}</Link>
          </div>
          <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" className="mt-1.5 h-11 rounded-xl" data-testid="login-password" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="login-remember-label">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-findo-indigo" data-testid="login-remember" />
          {t("a.rememberMe")}
        </label>
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold" data-testid="login-submit">
          {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("a.loggingIn")}</span> : t("a.login")}
        </Button>
      </form>
      <RecaptchaNotice className="mt-4 text-center" />

      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <span className="flex-1 h-px bg-border" /> {t("a.or")} <span className="flex-1 h-px bg-border" />
      </div>
      <GoogleAuthButton label={t("a.googleContinue")} />

      <p className="mt-6 text-center text-sm text-slate-500">
        {t("a.noAccount")}{" "}
        <Link to="/auth/register" className="font-semibold text-findo-indigo hover:underline">{t("a.createAccount")}</Link>
      </p>
    </AuthShell>
  );
}
